import { postRequest } from 'servos/apiRequests'
import axios from 'axios'
import { getTrackingId } from 'utils/tracking'

/**
 * Send a notification to Slack via the Rails backend
 * @param {string} message - The message to send
 * @returns {Promise<Object>} - Response from the backend
 */
export const sendSlackNotification = async (message) => {
  try {
    const response = await axios.post('/onboarding_funnel/send_slack_notification', {
      message: message
    })
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Parse user name into first and last name
 * @param {string} fullName - Full name string
 * @returns {Object} - Object with firstName and lastName
 */
const parseUserName = (fullName) => {
  const nameParts = fullName.split(' ').filter(part => part.trim() !== '')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ')
  return { firstName, lastName }
}

/**
 * Extract HubSpot tracking token from cookies
 * @returns {string} - HubSpot tracking token
 */
const getHubSpotToken = () => {
  return document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, "$1")
}

// ============================================================================
// MAIN REGISTRATION FUNCTION
// ============================================================================

/**
 * Handle user registration request
 * @param {Object} data - Registration data
 * @param {Function} onSuccess - Success callback
 * @param {Function} onFailure - Failure callback
 */
export const registrationRequest = async (data, onSuccess, onFailure) => {
  let userData = null
  
  try {
    const { firstName, lastName } = parseUserName(data.onboardingFunnel.userName)
    const hubspotToken = getHubSpotToken()
    const gclid = getTrackingId('gclid')
    const msclkid = getTrackingId('msclkid')
    
    userData = {
      email: data.onboardingFunnel.userEmail,
      mobile: data.onboardingFunnel.userMobile,
      firstName,
      lastName,
      hubspotToken,
      gclid,
      msclkid
    }

    const {
      loanPropertyType,
      loanPurpose,
      loanTarget,
      refinanceReason,
      hasFoundHome,
      spendAmount,
      loanAmount,
      rentAmount,
      locationAddress,
      locationSuburb,
      locationPostcode,
      locationState,
      userName,
      userEmail,
      userMobile,
      cashOutReason,
      cashOutAmount,
      termsAcceptedAt,
      termsAndConditions
    } = data.onboardingFunnel

    const requestData = {
      onboardingFunnel: {
        loanPropertyType,
        loanPurpose,
        loanTarget,
        refinanceReason,
        hasFoundHome,
        spendAmount,
        loanAmount,
        rentAmount,
        locationAddress,
        locationSuburb,
        locationPostcode,
        locationState,
        userName,
        userEmail,
        userMobile,
        cashOutReason,
        cashOutAmount,
        termsAcceptedAt,
        termsAndConditions
      }
    }
    
    let dbResponse = null
    let databaseSaveSucceeded = false
    await postRequest({ 
      url: '/onboarding_funnel.json', 
      data: requestData, 
      onSuccess: (response) => {
        dbResponse = response
        databaseSaveSucceeded = true
      }, 
      onFailure 
    })

    // postRequest handles HTTP errors by calling onFailure, but it does not
    // reject the promise. Do not create a HubSpot contact unless the Rails
    // endpoint confirmed that the signup was saved.
    if (!databaseSaveSucceeded) {
      throw new Error('Signup could not be saved')
    }
    
    if (gon.env === 'development') {
      return
    }
    await submitToHubSpot(userData)
    onSuccess(dbResponse)
  } catch (error) {
    console.error('❌ Registration failed:', error)
    const userInfo = userData 
      ? `${userData.firstName} ${userData.lastName} email: ${userData.email} mobile: ${userData.mobile}`
      : `email: ${data?.onboardingFunnel?.userEmail || 'unknown'}`
    await sendSlackNotification(`❌ Registration failed for ${userInfo}: ${error.message}`)
    throw error
  }
}

/**
 * Simplified HubSpot submission
 * @param {Object} userData - User information
 */
const submitToHubSpot = async (userData) => {
  try {
    if (!gon.services.hubspot_api_key) {
      throw new Error('HubSpot API key is missing')
    }
    
    const hubSpotURI = `https://api.hsforms.com/submissions/v3/integration/submit/6007498/${gon.services.hubspot_api_key}`
    
    const fields = [
      { name: 'email', value: userData.email },
      { name: 'mobilephone', value: userData.mobile },
      { name: 'firstname', value: userData.firstName },
      { name: 'lastname', value: userData.lastName }
    ]
    if (userData.msclkid) {
      fields.push({ name: 'msclkid', value: userData.msclkid })
    }

    const context = {
      pageUri: 'https://www.yard.com.au/onboarding_funnel/new',
      pageName: 'Save Your Progress'
    }
    
    // Only include hutk if it has a valid value - empty string causes 400 error
    if (userData.hubspotToken) {
      context.hutk = userData.hubspotToken
    }

    const hubSpotData = {
      submittedAt: new Date().getTime(),
      fields: fields,
      context: context
    }
    
    const response = await axios.post(hubSpotURI, hubSpotData)
    
    return response
  } catch (error) {
    console.error('❌ HubSpot error:', error)
    console.error('❌ HubSpot error response:', error.response?.data)
    
    // Include HubSpot error details in Slack for better debugging
    const hubspotErrorDetails = error.response?.data?.errors 
      ? JSON.stringify(error.response.data.errors)
      : error.response?.data?.message || error.message
    
    await sendSlackNotification(`❌ HubSpot failed: ${userData.firstName} ${userData.lastName} mobile: ${userData.mobile} email: ${userData.email} - ${hubspotErrorDetails}`)
    
    // Don't throw - let registration continue
    console.warn('⚠️ Registration completed but HubSpot failed')
  }
}
